import React, { useCallback, useEffect, useState } from 'react';
import {
  updateCustomerChangeRequeItem,
  getCustomerChangeRequestTasks,
  assignEngineer
} from '../services/api/implementation/impl';
import { useMutation, useQuery } from '@tanstack/react-query';
import Table from '../../shared/components/layout/Table';
import Modal from '../../shared/components/ui/Modal';
import FormInput from '../../shared/components/ui/FormInput';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '../../shared/components/ui/Button';
import toast from 'react-hot-toast';
import { transformData } from '../../super-admin/utils/transformData';
import Select from 'react-select';
import Search from '../../shared/components/ui/Search';
import LeftArrow from '../../shared/assets/svg/left_arrow.svg';
import BackArrow from '../../shared/assets/svg/backbtn.svg';
import { getUsers } from '../../super-admin/services/api/implementation/impl';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const ChangeRequestItems = ({ cRID, globalStatus, setShowTasks }) => {
  const userId = Cookies.get('userId');
  const [limit] = useState(10000);
  const [page, setPage] = useState(1);
  const [tasks, setTasks] = useState([]);
  const [OpenAddVendor, setOpenAddVendor] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [itemID, setItemID] = useState();
  const [assignedEngineer, setAssignedEngineer] = useState();
  const [allUsers, setAllUsers] = useState([{ id: null, lable: null }]);
  const [builderAction, setBuilderAction] = useState(false);
  const [engineerAction, setEngineerAction] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigned, setAssigned] = useState(false);
  // The budget/effort modal serves two callers: the builder editing the figures
  // ('Update'), and the engineer accepting them. Only the second sends a status.
  const [detailsMode, setDetailsMode] = useState('Update');
  const schema = yup.object().shape({
    budget: yup
      .number()
      .required('Budget is required')
      .positive('Budget must be a positive number'),
    effort: yup.number().required('Effort is required').positive('Effort must be a positive number')
  });

  const { data: AllUsers } = useQuery({
    queryKey: ['getUsers'],
    queryFn: () => getUsers(),
    enabled: true
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Change Request Items Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (AllUsers) {
      const filteredUsers = AllUsers?.filter((user) => user.vertical === 'Operations').map(
        (user) => ({
          id: user.id,
          label: user.name
        })
      );

      setAllUsers(filteredUsers);
    }
  }, [AllUsers]);

  const handleChange = (selectedOption) => {
    setAssignedEngineer(selectedOption?.id || null);
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm({
    resolver: yupResolver(schema)
  });

  // Create a query at the top level
  const { data: changeRequestTasks, refetch } = useQuery({
    queryKey: ['getCustomerChangeRequestTasks', cRID],
    queryFn: () => getCustomerChangeRequestTasks(cRID, page, limit),
    enabled: !!cRID
  });

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (changeRequestTasks) {
      setTasks(changeRequestTasks?.data);
      // Check if any task has an assigned engineer
      const isAssigned = changeRequestTasks?.data?.some(
        (task) => task?.customerChangeRequest?.engineer
      );
      setAssigned(isAssigned);

      // Check if all builder statuses are either "Accepted" or "Rejected"
      const allBuilderStatusComplete = changeRequestTasks?.data?.every(
        (task) => task?.builderStatus === 'Accepted' || task?.builderStatus === 'Rejected'
      );
      setBuilderAction(allBuilderStatusComplete);

      // Check if all engineer statuses are either "Accepted" or "Rejected"
      const allEngineerStatusComplete = changeRequestTasks?.data?.every(
        (task) => task?.engineerStatus === 'Accepted' || task?.engineerStatus === 'Rejected'
      );
      setEngineerAction(allEngineerStatusComplete);
    } else {
      setAssigned(false);
    }
  }, [changeRequestTasks]);

  // The API has returned this as a nested user; fall back to the flat id the
  // assign call writes, so a shape change degrades to hiding the buttons rather
  // than showing them to the wrong person.
  const engineerIdOf = (item) => {
    const ccr = item?.customerChangeRequest;
    return ccr?.engineer?.id ?? ccr?.engineer?.user_id ?? ccr?.engineer_id ?? null;
  };

  const isAssignedEngineer = (item) => {
    const engineerId = engineerIdOf(item);
    return Boolean(engineerId) && String(engineerId) === String(userId);
  };

  /**
   * Engineer approval was app-only until now: the site engineer accepted or
   * rejected in EngineerSingleChangeRequestOverView and the builder could only
   * watch the column here. Operations users at designation High have no app
   * access at all, so for them the request could never move. Same endpoint as
   * the builder verdict, different field.
   *
   * Gating copies the app: hidden once the request is Rejected outright or the
   * engineer has already accepted. A rejection can still be revisited.
   * Identity matters here in a way it does not in the app, whose list is
   * already scoped to the engineer server side — this page shows every row, so
   * without the check a builder could set the engineer's verdict.
   */
  const canEngineerAct = (item) =>
    isAssignedEngineer(item) &&
    item?.globalStatus !== 'Rejected' &&
    item?.engineerStatus !== 'Accepted';

  const formatData = (item, index) => {
    return {
      id: item.id,
      Requirements: `${item.title}`,
      Assigned_to: `${item.customerChangeRequest?.engineer?.name || 'N/A'}`,
      Effort: `${((item.builderStatus === 'Accepted' || item.engineerStatus === 'Accepted') && item.noOfDays) || '---'} Days`,
      Budget:
        ((item.builderStatus === 'Accepted' || item.engineerStatus === 'Accepted') &&
          item.budget) ||
        0,
      Status: item.globalStatus,
      actions: 'Actions',
      EApproval: !item.customerChangeRequest?.engineer?.name
        ? 'N/A'
        : canEngineerAct(item)
          ? 'EngineerApprovalOption'
          : item.engineerStatus,
      approval:
        ['Ongoing', 'Pending'].includes(item.engineerStatus) &&
        ['Ongoing', 'Pending'].includes(item.builderStatus)
          ? 'YesNoOption'
          : item.engineerStatus === 'Accepted' &&
              ['Ongoing', 'Pending'].includes(item.builderStatus)
            ? 'YesNoOption'
            : ['Ongoing', 'Pending'].includes(item.engineerStatus) &&
                item.builderStatus === 'Accepted'
              ? 'Accepted'
              : item.engineerStatus === 'Rejected' &&
                  ['Ongoing', 'Pending'].includes(item.builderStatus)
                ? 'YesNoOption'
                : ['Ongoing', 'Pending'].includes(item.engineerStatus) &&
                    item.builderStatus === 'Rejected'
                  ? 'Rejected'
                  : item.engineerStatus === 'Rejected' && item.builderStatus === 'Rejected'
                    ? 'Rejected'
                    : item.builderStatus // Optional fallback case
    };
  };

  const handleSendToCustomer = () => {
    const formattedData = {
      globalStatus: 'Approved'
    };
    handleTransferToCustomer({ id: cRID, payload: formattedData });
  };

  const { mutate: handleTransferToCustomer } = useMutation({
    mutationFn: ({ id, payload }) => assignEngineer(id, payload),

    onSuccess: (data) => {
      toast.success(`Sent To Customer Successfully`);
      refetch();
      setShowTasks(false);
    },
    onError: (err) => {
      console.error(`Error sending to customer`, err);
      toast.error(err?.response?.data?.message || err?.message || `Error sending to customer`);
    }
  });

  const TableData = tasks?.map((item, index) =>
    transformData(formatData(item, index), [
      'id',
      'Requirements',
      'Assigned_to',
      'Effort',
      'Budget',
      'Status',
      'actions',
      'EApproval',
      'approval'
    ])
  );

  const handleEdit = (id) => {
    const data = tasks.find((item) => Number(item.id) === Number(id));

    if (data.globalStatus === 'Rejected') {
      toast.error('Request Is Rejected');
      return;
    }
    if (data) {
      setItemID(id);
      setValue('budget', data.budget || ''); // Set the budget value
      setValue('effort', data.noOfDays || ''); // Set the effort value
      setDetailsMode('Update');
      setOpenAddVendor(true);
    }
  };

  const handleBudgetChange = useCallback(
    (val) => {
      const cleanedValue = val.replace(/\D/g, '').slice(0, 50); // Accepts only numbers, max length 50
      setValue('budget', cleanedValue);
    },
    [setValue]
  );

  const handleEffortChange = useCallback(
    (val) => {
      const cleanedValue = val.replace(/\D/g, '').slice(0, 100); // Accepts only numbers, max length 100
      setValue('effort', cleanedValue);
    },
    [setValue]
  );

  const { mutate: handleAction } = useMutation({
    mutationFn: ({ id, payload }) => updateCustomerChangeRequeItem(id, payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async (data, { status }) => {
      setIsSubmitting(false);
      toast.success(`Item ${status} Successfully`);

      // Ensure refetch is awaited before closing the modal
      await refetch();

      setOpenAddVendor(false);
    },
    onError: (err, { status }) => {
      setIsSubmitting(false);
      console.error(`Error ${status} Item`, err);
      toast.error(err?.response?.data?.message || err?.message || `Error ${status} Item`);
    }
  });

  const handleUpdate = (id, status) => {
    const data = tasks.find((item) => Number(item.id) === Number(id));
    if (!data) return;

    setItemID(id);

    const payload =
      status === 'Accepted'
        ? { builderStatus: 'Accepted' }
        : status === 'Rejected'
          ? { builderStatus: 'Rejected' }
          : status === 'Update'
            ? { budget: data.budget, totalDays: data.noOfDays }
            : {};

    handleAction({ id, payload, status });
  };

  const handleYes = (id) => handleUpdate(id, 'Accepted');

  const handleNo = (id) => handleUpdate(id, 'Rejected');

  const handleEngineerNo = (id) => {
    const data = tasks.find((item) => Number(item.id) === Number(id));
    if (!data || !canEngineerAct(data)) return;
    setItemID(id);
    handleAction({ id, payload: { engineerStatus: 'Rejected' }, status: 'Rejected' });
  };

  // Accepting confirms the effort and budget, so both have to be present. When
  // they already are, accept straight away rather than re-asking for figures
  // the engineer would only retype — the app does the same (directAcceptBudget).
  const handleEngineerYes = (id) => {
    const data = tasks.find((item) => Number(item.id) === Number(id));
    if (!data || !canEngineerAct(data)) return;

    setItemID(id);

    if (data.noOfDays && data.budget) {
      handleAction({
        id,
        payload: {
          engineerStatus: 'Accepted',
          noOfDays: data.noOfDays,
          budget: data.budget
        },
        status: 'Accepted'
      });
      return;
    }

    setValue('budget', data.budget || '');
    setValue('effort', data.noOfDays || '');
    setDetailsMode('EngineerAccept');
    setOpenAddVendor(true);
  };

  const onSubmitForm = (data) => {
    const isEngineerAccept = detailsMode === 'EngineerAccept';
    const formattedData = {
      budget: data.budget,
      noOfDays: data.effort,
      ...(isEngineerAccept ? { engineerStatus: 'Accepted' } : {})
    };

    handleAction({
      id: itemID,
      payload: formattedData,
      status: isEngineerAccept ? 'Accepted' : 'Update'
    });
    reset();
  };

  const handleAssignEngineer = () => {
    const formattedData = {
      engineer_id: assignedEngineer
    };
    handleAssign({ id: cRID, payload: formattedData });
  };

  const { mutate: handleAssign } = useMutation({
    mutationFn: ({ id, payload }) => assignEngineer(id, payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: (data) => {
      setIsSubmitting(false);
      toast.success(`Engineer Assigned Successfully`);
      refetch();
      setShowAssignModal(false);
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error(`Error assigning engineer`, err);
      toast.error(err?.response?.data?.message || err?.message || `Error assigning engineer`);
    }
  });

  return (
    <div className=" h-[28rem] relative flex flex-col gap-3">
      <div className="w-full flex justify-between items-center">
        <div className="flex gap-1 justify-start items-center font-medium text-[18px]">
          <div
            onClick={() => setShowTasks(false)}
            className="flex justify-start items-center cursor-pointer">
            <img height={30} width={30} src={LeftArrow} />
            Back
            <img className="rotate-180" src={BackArrow} />
          </div>
          Change Requests
        </div>
        {!['Approved', 'Accepted', 'Rejected'].includes(globalStatus) && (
          <Button
            onClick={() => {
              if (assigned) {
                if (builderAction && engineerAction) {
                  handleSendToCustomer();
                } else {
                  toast.error('Engineer Approval Is Pending');
                }
              } else {
                if (builderAction) {
                  setShowAssignModal(true);
                } else {
                  toast.error('Please Accept/Reject All The Requests');
                }
              }
            }}>
            {assigned ? 'Send To Customer' : 'Assign Engineer'}
          </Button>
        )}
      </div>
      <Table
        headers={[
          'Requirements',
          'Assigned to',
          'Effort',
          'Budget',
          'Status',
          'Action',
          'Engineer Approval',
          'Builder Approval'
        ]}
        data={TableData}
        identifier={'id'}
        onEdit={handleEdit}
        // handleRowClick={handleNavigateVendor}
        onYes={handleYes}
        onNo={handleNo}
        onEngineerYes={handleEngineerYes}
        onEngineerNo={handleEngineerNo}
        // isDeleteEnable={true}
        isEditEnable={true}
      />
      {OpenAddVendor && (
        <Modal
          isDirty={true}
          width={30}
          name={detailsMode === 'EngineerAccept' ? 'Confirm Effort And Budget' : 'Add Details'}
          onClose={() => setOpenAddVendor(false)}>
          <form onSubmit={handleSubmit(onSubmitForm)} className="flex flex-col ">
            <div className="flex justify-between items-center w-full">
              <FormInput
                width={48}
                label={'Enter Budget'}
                register_key={'budget'}
                minLength={3}
                isRequired={true}
                type={'text'}
                register={register}
                onType={handleBudgetChange}
                errors={errors.budget}
              />
              <FormInput
                width={48}
                label={'Enter Effort In Days'}
                register_key={'effort'}
                isRequired={true}
                register={register}
                type={'text'}
                onType={handleEffortChange}
                errors={errors.effort}
              />
            </div>
            <div className="w-full flex justify-end items-center space-x-4 mt-8">
              <Button type="button" onClick={() => setOpenAddVendor(false)} displayType="secondary">
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {detailsMode === 'EngineerAccept' ? 'Accept' : 'Submit'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
      {showAssignModal && (
        <Modal
          isDirty={true}
          width={30}
          name="Assign Engineer"
          onClose={() => setShowAssignModal(false)}>
          <Select
            width={40}
            isMulti={false}
            options={allUsers}
            getOptionLabel={(e) => e.label}
            getOptionValue={(e) => e.id} // ensures the value is tied to the `id`
            value={allUsers.find((option) => option.id === assignedEngineer) || null}
            onChange={handleChange}
            className="basic-multi-select"
            classNamePrefix="select Any Engineer"
          />
          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button type="button" onClick={() => setShowAssignModal(false)} displayType="secondary">
              Cancel
            </Button>
            <Button type="button" onClick={handleAssignEngineer} disabled={isSubmitting}>
              Assign
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ChangeRequestItems;
